import type { SavedDeviceIds, SelectedAdminFilters } from '$lib/components/models'
import type { PersistentStore } from '$lib/svelte-persistent-store-main/src/index'
import type { Client as ConversationsClient } from '@twilio/conversations'
import type { ContactCompany, Document, Notification, Subscription, Tag, Team, TeamUser, User } from 'fixee-server'
import type { NotificationType } from 'fixee-server/lib/services/notification/notification.shared'
import type { BrowserPermissions } from 'fixee-server/lib/services/portal/portal.shared'
import { getContext } from 'svelte'
import type { Writable } from 'svelte/store'
import { SIDEBAR } from '../utils'

export type AuthContextType = {
  user: Writable<User | undefined>
  value: Writable<string>
}

export type ApiStatus = 'connected' | 'disconnected' | 'authenticated'

export type RGPDAccept = {
  hasAcceptedCGU?: boolean
  hasAcceptedCookies?: boolean
}

export type GlobalVars = {
  openComingSoonModal?:
    | 'comingSoonAudioCall'
    | 'comingSoonPictureMode'
    | 'comingSoonRecordVocal'
    | 'comingSoonRequestVocal'
    | 'comingSoonRequestText'
  openVideoPermissionsWarning?: boolean
}

export type WindowSize = {
  width: number
  height: number
}

export type NotificationsInfos = {
  open: boolean
  loading: boolean
  nbUnseen: number
  nbUnseenIncomingPortals: number
  filterHasBeenSeen: undefined | boolean
  types: undefined | NotificationType[]
  portalRef_notifId_ToOpen: string | undefined
  reloadNotifications: (notificationIds: string[]) => void
  loadNotifications: (skip: number, filterHasBeenSeen: undefined | boolean, types?: NotificationType[]) => void
  hasLoadedAllTheNotifications: boolean
}

export type PortalPictureModeContext = {
  enabled: boolean
  startAudio?: 'in-app' | 'phone' // Which audio source to start with
}

export type PortalUISettings = {
  showHeader?: boolean
  showChatInput?: boolean
  showBorder?: boolean
  scrollToBottomForMessages?: boolean
  isOnlyMobileUI?: boolean
  onTakeChargeRedirectToPortal?: boolean
}

// General contexts
export const getCurrentUserContext = () => getContext<PersistentStore<User | undefined>>('currentUser')
export const getCurrentTeamUserContext = () => getContext<PersistentStore<TeamUser | undefined>>('currentTeamUser')
export const getTeamsContext = () => getContext<Writable<Array<Team>>>('teams')
export const getCurrentTeamContext = () => getContext<PersistentStore<Team>>('currentTeam')
export const getCurrentSubscriptionContext = () =>
  getContext<PersistentStore<Subscription | undefined>>('currentSubscription')
export const getApiStatusContext = () => getContext<Writable<ApiStatus>>('apiStatus')
export const getTagsContext = () => getContext<Writable<Tag[]>>('tags')
export const getChatClientContext = () => getContext<Writable<ConversationsClient | undefined>>('chatClient')

// Not validated yet, to keep?
export const getBrowserPermissionsContext = () => getContext<Writable<BrowserPermissions>>('permissions')
export const getDeviceIdsContext = () => getContext<Writable<SavedDeviceIds>>('deviceIds')
export const getRGPDAcceptContext = () => getContext<Writable<RGPDAccept>>('RGPDAccept')
export const getGlobalVarsContext = () => getContext<Writable<GlobalVars>>('globalVars')
export const getWindowSizeContext = () => getContext<Writable<WindowSize>>('windowSize')
export const getMyIssuerPortalsContext = () => getContext<Writable<undefined | boolean>>('myIssuerPortals')
export const getAdminSelectedFiltersContext = () => getContext<Writable<SelectedAdminFilters>>('adminSelectedFilters')

// Handler Notification
export const getNotificationsContext = () => getContext<Writable<Notification[]>>('notifications')
export const getNotificationsInfosContext = () => getContext<Writable<NotificationsInfos>>('notificationsInfos')

// Chatbot
export const getChatbotSelectedDocumentContext = () =>
  getContext<Writable<Document | undefined>>('chatbotSelectedDocument')

// Contacts
export const getContactCompaniesContext = () => getContext<Writable<ContactCompany[]>>('contactCompanies')

// Issuer view: user has no joined team (only personal space). Hotliner = at least one non-personal team.
export const getIsIssuerViewContext = () => getContext<Writable<boolean>>('isIssuerView')

// Sidebar
export const getSidebarContext = () => {
  const context = getContext<Writable<{ isOpen: boolean; isCollapsed: boolean }>>(SIDEBAR)
  if (!context) {
    throw new Error('SIDEBAR context is missing. Make sure SIDEBAR is set in the context.')
  }
  return context
}
